import { InputOtp } from '@heroui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Clock, MapPin, Minus, Plus, Trash2 } from 'lucide-react';
import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { axiosInstance } from 'src/api';
import { Error, Modal, Spinner } from 'src/components';
import { useToast } from 'src/hooks';
import { useGetRentalFromFirestore, useQueryHelper } from 'src/queries';
import { rentalFormSchema } from 'src/schemas';
import { useLanguageState, useOrganizationState } from 'src/state';
import { RentalFormFields, RentalRegistrationData } from 'src/types';

/* eslint-disable max-lines */
import ErrorImage from '../../assets/images/link-sent-error.png';
import SuccesImage from '../../assets/images/link-sent-success.png';
import {
  ActionContainer, ActionContainerPrice,
  Address,
  Banner,
  Button,
  ButtonLink,
  Checkbox,
  CodeContainer,
  Container,
  CounterButton,
  CounterContainer,
  Description,
  Divider,
  Form,
  FormContainer,
  FormLabel,
  FormText,
  FormWrapper,
  Input,
  InputLabel,
  InputsContainer,
  Label,
  LabelContainer,
  LoaderTitle,
  PersonTitle,
  RowContainer,
  ScrollContainer,
  StatusImage,
  StatusText,
  SummaryContainer,
  Title,
  TrashButton,
} from './Rental.styled';

export const Rental: FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { showError, showPromise } = useToast();
  const { organizationData } = useOrganizationState();
  const { language: currentLang } = useLanguageState();
  const rentalDocument = useGetRentalFromFirestore(organizationData.hotel || '', id || '');
  const { data: rental, refetch } = rentalDocument;
  const { isLoading, isError } = useQueryHelper({ queries: [rentalDocument] });
  const [quantity, setQuantity] = useState(1);
  const [price, setPrice] = useState(0);
  const [hoursResult, setHoursResult] = useState(1);
  const [formStatus, setFormStatus] = useState<'form' | 'form_filled' | 'email_verification' | 'none'>('form');
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showRegulationModal, setShowRegulationModal] = useState(false);
  const [acceptRegulations, setAcceptRegulations] = useState(false);
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [verificationError, setVerificationError] = useState<boolean>(false);
  const [userCode, setUserCode] = useState('');
  const [timeLeft, setTimeLeft] = useState(120);
  const [timerActive, setTimerActive] = useState(false);

  const methods = useForm<RentalFormFields>({
    defaultValues: {
      firstName: '',
      roomNumber: 0,
      dateFrom: '',
      ...(rental?.multipleDayRent ? { dateTo: '' } : {}),
      ...(rental?.code_disabled ? {} : { email: '' }),
      ...(rental?.multipleDayRent ? {} : { timeFrom: '' }),
      ...(rental?.multipleDayRent ? {} : { timeTo: '' }),
      timeTo: '',
    },
    mode: 'all',
    resolver: zodResolver(rentalFormSchema({
      includeEmail: !rental?.code_disabled,
      includeHours: !rental?.multipleDayRent,
    })),
  });

  const { reset } = methods;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const rentalRegistration = async (data: RentalRegistrationData) => axiosInstance.post(
    'createRentalRegistrationUserDocumentFirebase',
    {
      data,
      hotelId: organizationData.hotel,
    },
    { headers: { 'Content-Type': 'application/json' } },
  );

  const rentalMutation = useMutation({
    mutationFn: rentalRegistration,
    onSuccess: () => {
      reset();
    },

    onError: () => {
      showError('Błąd zapisu!');
    },
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const onSubmit = (data: RentalFormFields) => {
    if (!data) return;
    if (formStatus === 'form') {
      setShowSummaryModal(true);
      setFormStatus('form_filled');
      return;
    }
    if (
      (formStatus === 'email_verification' && userCode === verificationCode)
      || (rental?.code_disabled && formStatus === 'form_filled')) {
      const promise = rentalMutation.mutateAsync({
        item_id: id,
        timeFrom: data.timeFrom,
        timeTo: data.timeTo,
        dateFrom: data.dateFrom,
        dateTo: rental?.multipleDayRent ? data.dateTo : data.dateFrom,
        name: data.firstName,
        room: data.roomNumber,
        email: data.email || '',
        quantity,
        price: rental?.price,
      });
      showPromise(
        promise,
        {
          pending: 'Trwa zapis...',
          success: 'Dane przesłane poprawnie.',
          error: 'Błąd zapisu!',
        },
      );
    }
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const sendVerificationCode = async () => {
    const email = methods.getValues('email');
    const name = methods.getValues('firstName');
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setVerificationCode(code);
    try {
      const response = axiosInstance.post(
        '/EmailVerifiyApi',
        { email, code, name },
        { headers: { 'Content-Type': 'application/json' } },
      );

      setFormStatus('email_verification');
      return response;
    } catch (error) {
      console.error('Błąd podczas wysyłaniu kodu:', error); return null;
    }
  };

  const resetForm = () : void => {
    reset();
    setPrice(parseInt(rental?.price || '0', 10));
    setQuantity(1);
    setFormStatus('form');
  };

  const calculateHours = (timeRange: string) => {
    if (!timeRange) return;
    const [start, end] = timeRange.split(' - ');
    const convertTo24HourFormat = (time: string) :string => {
      const [timeString, modifier] = time.split(' ');
      const parts = timeString.split(':').map(Number);
      let hoursParts = parts[0];
      const minutes = parts[1];
      if (modifier === 'PM' && hoursParts < 12) hoursParts += 12;
      if (modifier === 'AM' && hoursParts === 12) hoursParts = 0;

      return `${hoursParts.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };

    const start24 = convertTo24HourFormat(start);
    const end24 = convertTo24HourFormat(end);
    const startTime = new Date(`1970-01-01T${start24}:00Z`);
    const endTime = new Date(`1970-01-01T${end24}:00Z`);

    const differenceInMs = endTime.getTime() - startTime.getTime();
    const hours = Math.ceil(differenceInMs / (1000 * 60 * 60));
    setHoursResult(hours);
  };

  useEffect(() => {
    if (!price && rental && rental?.price) {
      setPrice(parseInt(rental?.price, 10) * hoursResult * quantity);
    } else {
      setPrice(parseInt(rental?.price || '0', 10) * hoursResult * quantity);
    }
  }, [quantity, hoursResult]);

  useEffect(() => {
    if (methods.getValues('timeFrom') && methods.getValues('timeTo')) {
      calculateHours(`${methods.getValues('timeFrom')} - ${methods.getValues('timeTo')}`);
    }
  }, [methods.getValues('timeFrom'), methods.getValues('timeTo')]);

  useEffect(() => {
    if (userCode.length > 0) {
      setVerificationError(false);
    }

    if (userCode.length === 6) {
      const isCodeCorrect = userCode === verificationCode;

      if (isCodeCorrect) {
        setVerificationError(false);
      } else {
        setVerificationError(true);
        setUserCode('');
      }
    }
  }, [userCode, verificationCode]);

  useEffect(() => {
    if (rental?.rentFormAvailable === false) {
      setFormStatus('none');
    } else {
      setFormStatus('form');
    }
  }, [rental?.rentFormAvailable]);

  const startTimer = (): void => {
    setTimeLeft(120);
    setTimerActive(true);
  };

  const resendVerificationCode = (): void => {
    startTimer();
    sendVerificationCode();
    setVerificationError(false);
  };

  const emailVerificationHandler = (): void => {
    setFormStatus('email_verification');
    sendVerificationCode();
    startTimer();
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (timerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(interval);
            setTimerActive(false);
            setVerificationCode('');
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [timerActive, timeLeft]);

  if (isLoading) {
    return <Spinner global />;
  }

  if (isError) {
    return <Error errorTitle="Bład pobierania zasobu" action={refetch} />;
  }

  return (
    <ScrollContainer>
      <Title>{rental?.title[currentLang]}</Title>
      <Banner src={rental?.cover} />
      <LabelContainer>
        <Label>
          <Clock size={20} color="#1C1C1C" />
          <Address>
            {rental?.time_from} - {rental?.time_to}
          </Address>
        </Label>
        <Label>
          <MapPin size={20} color="#1C1C1C" />
          <Address>
            {rental?.location}
          </Address>
        </Label>
      </LabelContainer>
      <Description>{rental?.description[currentLang]}</Description>
      {rentalMutation.isPending && (
      <Container>
        <Spinner />
        <LoaderTitle>{t('event_screen.loading')}</LoaderTitle>
      </Container>
      )}
      {!rentalMutation.isPending && !rentalMutation.isSuccess && !rentalMutation.isError && formStatus !== 'none' && (
        <FormContainer>
          <Modal
            size="xl"
            isOpen={showSummaryModal}
            onClose={() => {
              setShowSummaryModal(false);
              setFormStatus('form');
            }}
            title={formStatus === 'form_filled' ? t('rent_screen.verify_order') : t('rent_screen.email_verification')}
          >
            {formStatus === 'email_verification' && (
              <CodeContainer key="modal">
                <FormText>
                  {t('rent_screen.email_verification_info')}:
                  <FormLabel>{methods.getValues('email')}</FormLabel>
                </FormText>
                <InputOtp size="lg" length={6} value={userCode} onValueChange={setUserCode} />
                {verificationError && <FormText style={{ color: 'red' }}>{t('rent_screen.code_error')}</FormText>}
                {timerActive && timeLeft > 0 ? (
                  <FormText>{`${t('rent_screen.resend_code_in')}: ${Math.floor(timeLeft / 60)}:${String(
                    timeLeft % 60,
                  ).padStart(2, '0')}`}
                  </FormText>
                ) : (
                  <Button
                    onClick={resendVerificationCode}
                    type="button"
                  >
                    {t('rent_screen.resend_code')}
                  </Button>
                )}
                {userCode.length === 6 && userCode === verificationCode && (
                  <Button
                    onClick={methods.handleSubmit(onSubmit)}
                    type="submit"
                  >
                    {t('rent_screen.confirm')}
                  </Button>
                )}
              </CodeContainer>
            )}
            {formStatus === 'form_filled' && (
              <SummaryContainer key="modal">
                <RowContainer>
                  <FormLabel>{t('rent_screen.ordering_person')}:</FormLabel>
                  <FormText>{methods.getValues('firstName')}</FormText>
                </RowContainer>
                <RowContainer>
                  <FormLabel>{t('form.room_number')}:</FormLabel>
                  <FormText>{methods.getValues('roomNumber')}</FormText>
                </RowContainer>
                {!rental?.code_disabled && (
                  <RowContainer>
                    <FormLabel>{t('rent_screen.email')}:</FormLabel>
                    <FormText>{methods.getValues('email')}</FormText>
                  </RowContainer>
                )}
                <RowContainer>
                  <FormLabel>{t('rent_screen.rental_date')}:</FormLabel>
                  <FormText>{methods.getValues('dateFrom')} - {methods.getValues('dateTo')}</FormText>
                </RowContainer>
                {!rental?.multipleDayRent && (
                  <RowContainer>
                    <FormLabel>{t('form.hours_range')}:</FormLabel>
                    <FormText>{methods.getValues('timeFrom')} - {methods.getValues('timeTo')}</FormText>
                  </RowContainer>
                )}
                <RowContainer>
                  <FormLabel>{t('rent_screen.quantity')}:</FormLabel>
                  <FormText>{quantity}</FormText>
                </RowContainer>
                <Divider />
                <RowContainer>
                  <FormLabel>{t('rent_screen.price')}:</FormLabel>
                  <Title>{price}</Title>
                </RowContainer>
                <FormWrapper>
                  {rental?.accept_regulation_required === true && (
                    <RowContainer>
                      <Checkbox
                        type="checkbox"
                        checked={acceptRegulations}
                        onChange={() => setAcceptRegulations(!acceptRegulations)}
                      />
                      {rental?.regulation_file?.[currentLang] ? (
                        <ButtonLink
                          target="_blank"
                          href={rental?.regulation_file?.[currentLang]}
                          type="button"
                        >
                          {t('rent_screen.regulation')}
                        </ButtonLink>
                      ) : (
                        <Button
                          onClick={() => setShowRegulationModal(true)}
                          type="button"
                        >
                          {t('rent_screen.regulation')}
                        </Button>
                      )}
                    </RowContainer>
                  )}
                  <RowContainer>
                    {rental?.accept_regulation_required === true ? (
                      <Button
                        style={{
                          background: acceptRegulations
                            ? '#1C1C1C' : '#F4F4F5',
                        }}
                        disabled={!acceptRegulations}
                        type={rental?.code_disabled ? 'submit' : 'button'}
                        onClick={
                          rental?.code_disabled
                            ? methods.handleSubmit(onSubmit)
                            : emailVerificationHandler
                       }
                      >
                        {t('rent_screen.next_step')}
                      </Button>
                    ) : (
                      <Button
                        type={rental?.code_disabled ? 'submit' : 'button'}
                        onClick={
                          rental?.code_disabled
                            ? methods.handleSubmit(onSubmit)
                            : emailVerificationHandler
                      }
                      >
                        {t('rent_screen.next_step')}
                      </Button>
                    )}
                  </RowContainer>
                </FormWrapper>
              </SummaryContainer>
            )}
          </Modal>
          <Modal
            isOpen={showRegulationModal}
            onClose={() => {
              setShowRegulationModal(false);
              setFormStatus('form');
            }}
            title="Regulamin"
          >
            <FormText>{rental?.regulation_text?.[currentLang]}</FormText>
          </Modal>
          <Title>{t('rent_screen.rent')}</Title>
          <Form onSubmit={methods.handleSubmit(onSubmit)}>
            <InputsContainer>
              <Input
                type="text"
                placeholder={t('form.firstname')}
                {...methods.register('firstName')}
                error={methods.formState.errors.firstName?.message}
              />
              <Input
                type="number"
                placeholder={t('form.room_number')}
                {...methods.register('roomNumber', { valueAsNumber: true })}
                error={methods.formState.errors.roomNumber?.message}
              />
            </InputsContainer>
            <InputsContainer>
              {rental?.code_disabled !== true && (
              <Input
                type="email"
                placeholder="E-mail"
                {...methods.register('email')}
                error={methods.formState.errors.email?.message}
              />
              )}
              {!rental?.multipleDayRent && (
                <Input
                  type="date"
                  placeholder="Data"
                  {...methods.register('dateFrom')}
                  error={methods.formState.errors.dateFrom?.message}
                />
              )}
            </InputsContainer>
            {rental?.multipleDayRent && (
              <>
                <InputLabel htmlFor="timeFrom">{t('form.date')}</InputLabel>
                <FormWrapper>
                  <RowContainer>
                    <InputLabel htmlFor="timeFrom">{t('form.from')}:</InputLabel>
                    <Input
                      type="date"
                      placeholder="Data"
                      {...methods.register('dateFrom')}
                      error={methods.formState.errors.dateFrom?.message}
                    />
                  </RowContainer>
                  <RowContainer>
                    <InputLabel htmlFor="timeFrom">{t('form.to')}:</InputLabel>
                    <Input
                      type="date"
                      placeholder="Data"
                      {...methods.register('dateTo')}
                      error={methods.formState.errors.dateTo?.message}
                    />
                  </RowContainer>
                </FormWrapper>
              </>
            )}
            {!rental?.multipleDayRent && (
              <>
                <InputLabel htmlFor="timeFrom">{t('form.hours_range')}</InputLabel>
                <FormWrapper>
                  <RowContainer>
                    <InputLabel htmlFor="timeFrom">{t('form.from')}:</InputLabel>
                    <Input
                      type="time"
                      placeholder="Od"
                      {...methods.register('timeFrom')}
                      error={methods.formState.errors.timeFrom?.message}
                    />
                  </RowContainer>
                  <RowContainer>
                    <InputLabel htmlFor="timeFrom">{t('form.to')}:</InputLabel>
                    <Input
                      type="time"
                      placeholder="Do"
                      {...methods.register('timeTo')}
                      error={methods.formState.errors.timeTo?.message}
                    />
                  </RowContainer>
                </FormWrapper>
              </>
            )}
            <FormWrapper>
              <RowContainer>
                <PersonTitle>{t('rent_screen.quantity')}:</PersonTitle>
                <CounterContainer>
                  <CounterButton
                    type="button"
                    onClick={() => {
                      if (quantity > 1) {
                        setQuantity(quantity - 1);
                      }
                    }}
                  ><Minus size={14} />
                  </CounterButton>
                  <PersonTitle>{quantity}</PersonTitle>
                  <CounterButton
                    type="button"
                    onClick={() => {
                      setQuantity(quantity + 1);
                    }}
                  ><Plus size={14} />
                  </CounterButton>
                </CounterContainer>
              </RowContainer>
            </FormWrapper>
            <FormWrapper>
              <ActionContainerPrice>
                {t('rent_screen.price')}: <Title>{price} zł</Title>
              </ActionContainerPrice>
              <ActionContainer>
                <TrashButton onClick={resetForm}><Trash2 size={16} /></TrashButton>
                <Button
                  onClick={() => methods.handleSubmit(onSubmit)}
                  type="submit"
                >{t('rent_screen.confirm')}
                </Button>
              </ActionContainer>
            </FormWrapper>
          </Form>
        </FormContainer>
      )}
      {rentalMutation.isSuccess && (
      <Container>
        <StatusImage src={SuccesImage} alt="success" />
        <StatusText>{t('rent_screen.success_title')}</StatusText>
      </Container>
      )}
      {rentalMutation.isError && (
      <Container>
        <StatusImage src={ErrorImage} alt="error" />
        <StatusText>{t('rent_screen.error_title')}</StatusText>
      </Container>
      )}
    </ScrollContainer>
  );
};
